/**
 * AuthUI - Authentication UI components
 * Creates and manages the login modal and user status display
 */

import { authService } from './AuthService';
import type { User } from './types';

export class AuthUI {
  private modalElement: HTMLDivElement | null = null;
  private userStatusElement: HTMLDivElement | null = null;
  private dropdownVisible: boolean = false;

  constructor() {
    // Subscribe to auth state changes
    authService.onAuthStateChange((user) => {
      this.updateUserStatus(user);
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (this.dropdownVisible && this.userStatusElement) {
        const dropdown = this.userStatusElement.querySelector('.auth-dropdown');
        const trigger = this.userStatusElement.querySelector('.auth-user-trigger');
        if (dropdown && trigger && !dropdown.contains(e.target as Node) && !trigger.contains(e.target as Node)) {
          this.hideDropdown();
        }
      }
    });
  }

  /**
   * Create and show the login modal
   */
  showLoginModal(): void {
    if (this.modalElement) {
      this.modalElement.style.display = 'flex';
      return;
    }

    this.modalElement = document.createElement('div');
    this.modalElement.className = 'auth-modal-overlay';
    this.modalElement.innerHTML = this.getModalHTML();

    document.body.appendChild(this.modalElement);

    this.attachModalEventListeners();
  }

  /**
   * Hide the login modal
   */
  hideLoginModal(): void {
    if (this.modalElement) {
      this.modalElement.style.display = 'none';
    }
  }

  /**
   * Remove the modal from DOM
   */
  destroyModal(): void {
    if (this.modalElement) {
      this.modalElement.remove();
      this.modalElement = null;
    }
  }

  /**
   * Get the modal HTML template
   */
  private getModalHTML(): string {
    const isAuthAvailable = authService.isAuthAvailable();
    const authUnavailableNote = !isAuthAvailable 
      ? `<div class="auth-warning">
          <span class="auth-warning-icon">⚠️</span>
          Authentication is not configured. You can still play locally!
        </div>`
      : '';

    return `
      <div class="auth-modal">
        <button class="auth-modal-close" aria-label="Close">&times;</button>
        <h2 class="auth-modal-title">Welcome to Ariadne's String</h2>
        
        ${authUnavailableNote}

        <div class="auth-tabs">
          <button class="auth-tab active" data-tab="login">Sign In</button>
          <button class="auth-tab" data-tab="signup">Sign Up</button>
        </div>

        <div class="auth-tab-content active" data-content="login">
          <div class="auth-oauth-buttons">
            <button class="auth-btn auth-btn-google" ${!isAuthAvailable ? 'disabled' : ''}>
              <svg class="auth-btn-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
            <button class="auth-btn auth-btn-github" ${!isAuthAvailable ? 'disabled' : ''}>
              <svg class="auth-btn-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              Continue with GitHub
            </button>
          </div>

          <div class="auth-divider">
            <span>or</span>
          </div>

          <form class="auth-form" id="loginForm">
            <div class="auth-form-group">
              <label for="loginEmail">Email</label>
              <input type="email" id="loginEmail" placeholder="you@example.com" required ${!isAuthAvailable ? 'disabled' : ''}>
            </div>
            <div class="auth-form-group">
              <label for="loginPassword">Password</label>
              <input type="password" id="loginPassword" placeholder="••••••••" required ${!isAuthAvailable ? 'disabled' : ''}>
            </div>
            <button type="submit" class="auth-btn auth-btn-primary" ${!isAuthAvailable ? 'disabled' : ''}>
              Sign In
            </button>
          </form>
        </div>

        <div class="auth-tab-content" data-content="signup">
          <form class="auth-form" id="signupForm">
            <div class="auth-form-group">
              <label for="signupUsername">Username</label>
              <input type="text" id="signupUsername" placeholder="coolplayer123" required ${!isAuthAvailable ? 'disabled' : ''}>
            </div>
            <div class="auth-form-group">
              <label for="signupEmail">Email</label>
              <input type="email" id="signupEmail" placeholder="you@example.com" required ${!isAuthAvailable ? 'disabled' : ''}>
            </div>
            <div class="auth-form-group">
              <label for="signupPassword">Password</label>
              <input type="password" id="signupPassword" placeholder="••••••••" minlength="6" required ${!isAuthAvailable ? 'disabled' : ''}>
            </div>
            <button type="submit" class="auth-btn auth-btn-primary" ${!isAuthAvailable ? 'disabled' : ''}>
              Create Account
            </button>
          </form>
        </div>

        <div class="auth-divider">
          <span>or</span>
        </div>

        <button class="auth-btn auth-btn-guest" id="guestBtn" ${!isAuthAvailable ? 'disabled' : ''}>
          Continue as Guest
        </button>

        <p class="auth-footer-text">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>

        <div class="auth-error" id="authError" style="display: none;"></div>
      </div>
    `;
  }

  /**
   * Attach event listeners to modal elements
   */
  private attachModalEventListeners(): void {
    if (!this.modalElement) return;

    // Close button
    const closeBtn = this.modalElement.querySelector('.auth-modal-close');
    closeBtn?.addEventListener('click', () => this.hideLoginModal());

    // Click outside to close
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) {
        this.hideLoginModal();
      }
    });

    // Tab switching
    const tabs = this.modalElement.querySelectorAll('.auth-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = tab.getAttribute('data-tab');
        this.switchTab(tabName!);
      });
    });

    // OAuth buttons
    const googleBtn = this.modalElement.querySelector('.auth-btn-google');
    googleBtn?.addEventListener('click', () => this.handleGoogleSignIn());

    const githubBtn = this.modalElement.querySelector('.auth-btn-github');
    githubBtn?.addEventListener('click', () => this.handleGitHubSignIn());

    // Login form
    const loginForm = this.modalElement.querySelector('#loginForm') as HTMLFormElement;
    loginForm?.addEventListener('submit', (e) => this.handleEmailLogin(e));

    // Signup form
    const signupForm = this.modalElement.querySelector('#signupForm') as HTMLFormElement;
    signupForm?.addEventListener('submit', (e) => this.handleEmailSignup(e));

    // Guest button
    const guestBtn = this.modalElement.querySelector('#guestBtn');
    guestBtn?.addEventListener('click', () => this.handleGuestSignIn());
  }

  /**
   * Switch between login and signup tabs
   */
  private switchTab(tabName: string): void {
    if (!this.modalElement) return;

    // Update tab buttons
    const tabs = this.modalElement.querySelectorAll('.auth-tab');
    tabs.forEach(tab => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === tabName);
    });

    // Update tab content
    const contents = this.modalElement.querySelectorAll('.auth-tab-content');
    contents.forEach(content => {
      content.classList.toggle('active', content.getAttribute('data-content') === tabName);
    });

    this.clearError();
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    if (!this.modalElement) return;

    const errorEl = this.modalElement.querySelector('#authError') as HTMLDivElement;
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }
  }

  /**
   * Clear error message
   */
  private clearError(): void {
    if (!this.modalElement) return;

    const errorEl = this.modalElement.querySelector('#authError') as HTMLDivElement;
    if (errorEl) {
      errorEl.style.display = 'none';
    }
  }

  /**
   * Set loading state on buttons
   */
  private setLoading(loading: boolean): void {
    if (!this.modalElement) return;

    const buttons = this.modalElement.querySelectorAll('.auth-btn');
    buttons.forEach(btn => {
      (btn as HTMLButtonElement).disabled = loading;
    });
  }

  /**
   * Handle Google sign in
   */
  private async handleGoogleSignIn(): Promise<void> {
    this.clearError();
    this.setLoading(true);

    const { error } = await authService.signInWithGoogle();
    
    this.setLoading(false);
    
    if (error) {
      this.showError(error.message);
    }
    // OAuth redirects, so no need to hide modal on success
  }

  /**
   * Handle GitHub sign in
   */
  private async handleGitHubSignIn(): Promise<void> {
    this.clearError();
    this.setLoading(true);

    const { error } = await authService.signInWithGitHub();
    
    this.setLoading(false);
    
    if (error) {
      this.showError(error.message);
    }
    // OAuth redirects, so no need to hide modal on success
  }

  /**
   * Handle email login form submission
   */
  private async handleEmailLogin(e: Event): Promise<void> {
    e.preventDefault();
    this.clearError();

    const form = e.target as HTMLFormElement;
    const email = (form.querySelector('#loginEmail') as HTMLInputElement).value;
    const password = (form.querySelector('#loginPassword') as HTMLInputElement).value;

    this.setLoading(true);
    const { error } = await authService.signInWithEmail(email, password);
    this.setLoading(false);

    if (error) {
      this.showError(error.message);
    } else {
      this.hideLoginModal();
    }
  }

  /**
   * Handle email signup form submission
   */
  private async handleEmailSignup(e: Event): Promise<void> {
    e.preventDefault();
    this.clearError();

    const form = e.target as HTMLFormElement;
    const username = (form.querySelector('#signupUsername') as HTMLInputElement).value;
    const email = (form.querySelector('#signupEmail') as HTMLInputElement).value;
    const password = (form.querySelector('#signupPassword') as HTMLInputElement).value;

    this.setLoading(true);
    const { error } = await authService.signUpWithEmail(email, password, username);
    this.setLoading(false);

    if (error) {
      this.showError(error.message);
    } else {
      this.showError('Check your email to confirm your account!');
    }
  }

  /**
   * Handle guest sign in
   */
  private async handleGuestSignIn(): Promise<void> {
    this.clearError();
    this.setLoading(true);

    const { error } = await authService.signInAnonymously();
    
    this.setLoading(false);
    
    if (error) {
      this.showError(error.message);
    } else {
      this.hideLoginModal();
    }
  }

  /**
   * Create the user status header element
   */
  createUserStatusElement(): HTMLDivElement {
    this.userStatusElement = document.createElement('div');
    this.userStatusElement.className = 'auth-user-status';
    
    // Show loading state if auth is still loading
    if (authService.isLoading()) {
      this.userStatusElement.innerHTML = `
        <div class="auth-loading-status">
          <div class="auth-loading-spinner"></div>
        </div>
      `;
    } else {
      this.updateUserStatus(authService.getCurrentUser());
    }
    
    return this.userStatusElement;
  }

  /**
   * Update the user status display
   */
  updateUserStatus(user: User | null): void {
    if (!this.userStatusElement) return;

    if (user) {
      if (user.isGuest) {
        this.userStatusElement.innerHTML = `
          <div class="auth-guest-status">
            <span class="auth-guest-badge">Guest</span>
            <button class="auth-btn-small auth-btn-create-account" id="createAccountBtn">
              Create Account
            </button>
          </div>
        `;

        const createAccountBtn = this.userStatusElement.querySelector('#createAccountBtn');
        createAccountBtn?.addEventListener('click', () => this.showConvertGuestModal());
      } else {
        this.userStatusElement.innerHTML = `
          <div class="auth-user-info">
            <button class="auth-user-trigger">
              ${user.avatarUrl 
                ? `<img src="${user.avatarUrl}" alt="${user.username}" class="auth-avatar">`
                : `<div class="auth-avatar-placeholder">${user.username.charAt(0).toUpperCase()}</div>`
              }
              <span class="auth-username">${user.displayName || user.username}</span>
              <svg class="auth-dropdown-arrow" viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M7 10l5 5 5-5z"/>
              </svg>
            </button>
            <div class="auth-dropdown" style="display: none;">
              <div class="auth-dropdown-header">
                <div class="auth-dropdown-name">${user.displayName || user.username}</div>
                <div class="auth-dropdown-email">${user.email || 'No email'}</div>
              </div>
              <div class="auth-dropdown-divider"></div>
              <button class="auth-dropdown-item" id="profileBtn">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                Profile
              </button>
              <button class="auth-dropdown-item auth-dropdown-signout" id="signOutBtn">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                </svg>
                Sign Out
              </button>
            </div>
          </div>
        `;

        this.attachUserStatusListeners();
      }
    } else {
      this.userStatusElement.innerHTML = `
        <button class="auth-btn-small auth-btn-signin" id="signInBtn">
          Sign In
        </button>
      `;

      const signInBtn = this.userStatusElement.querySelector('#signInBtn');
      signInBtn?.addEventListener('click', () => this.showLoginModal());
    }
  }

  /**
   * Attach event listeners to user status elements
   */
  private attachUserStatusListeners(): void {
    if (!this.userStatusElement) return;

    const trigger = this.userStatusElement.querySelector('.auth-user-trigger');
    trigger?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleDropdown();
    });

    const signOutBtn = this.userStatusElement.querySelector('#signOutBtn');
    signOutBtn?.addEventListener('click', () => this.handleSignOut());

    const profileBtn = this.userStatusElement.querySelector('#profileBtn');
    profileBtn?.addEventListener('click', () => {
      // For now, just close the dropdown
      this.hideDropdown();
      // TODO: Show profile modal
    });
  }

  /**
   * Toggle the user dropdown
   */
  private toggleDropdown(): void {
    if (this.dropdownVisible) {
      this.hideDropdown();
    } else {
      this.showDropdown();
    }
  }

  /**
   * Show the dropdown
   */
  private showDropdown(): void {
    if (!this.userStatusElement) return;

    const dropdown = this.userStatusElement.querySelector('.auth-dropdown') as HTMLDivElement;
    if (dropdown) {
      dropdown.style.display = 'block';
      this.dropdownVisible = true;
    }
  }

  /**
   * Hide the dropdown
   */
  private hideDropdown(): void {
    if (!this.userStatusElement) return;

    const dropdown = this.userStatusElement.querySelector('.auth-dropdown') as HTMLDivElement;
    if (dropdown) {
      dropdown.style.display = 'none';
      this.dropdownVisible = false;
    }
  }

  /**
   * Handle sign out
   */
  private async handleSignOut(): Promise<void> {
    this.hideDropdown();
    await authService.signOut();
  }

  /**
   * Show modal to convert guest to full account
   */
  showConvertGuestModal(): void {
    const modal = document.createElement('div');
    modal.className = 'auth-modal-overlay';
    modal.innerHTML = `
      <div class="auth-modal">
        <button class="auth-modal-close" aria-label="Close">&times;</button>
        <h2 class="auth-modal-title">Create Your Account</h2>
        <p class="auth-modal-subtitle">Save your progress and access your account anywhere!</p>
        
        <form class="auth-form" id="convertGuestForm">
          <div class="auth-form-group">
            <label for="convertUsername">Username</label>
            <input type="text" id="convertUsername" placeholder="coolplayer123" required>
          </div>
          <div class="auth-form-group">
            <label for="convertEmail">Email</label>
            <input type="email" id="convertEmail" placeholder="you@example.com" required>
          </div>
          <div class="auth-form-group">
            <label for="convertPassword">Password</label>
            <input type="password" id="convertPassword" placeholder="••••••••" minlength="6" required>
          </div>
          <button type="submit" class="auth-btn auth-btn-primary">
            Create Account
          </button>
        </form>
        
        <div class="auth-error" id="convertError" style="display: none;"></div>
      </div>
    `;

    document.body.appendChild(modal);

    // Close button
    const closeBtn = modal.querySelector('.auth-modal-close');
    closeBtn?.addEventListener('click', () => modal.remove());

    // Click outside to close
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });

    // Form submission
    const form = modal.querySelector('#convertGuestForm') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();

      const username = (modal.querySelector('#convertUsername') as HTMLInputElement).value;
      const email = (modal.querySelector('#convertEmail') as HTMLInputElement).value;
      const password = (modal.querySelector('#convertPassword') as HTMLInputElement).value;

      const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Creating...';

      const { error } = await authService.convertGuestToUser(email, password, username);

      submitBtn.disabled = false;
      submitBtn.textContent = 'Create Account';

      if (error) {
        const errorEl = modal.querySelector('#convertError') as HTMLDivElement;
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.style.display = 'block';
        }
      } else {
        modal.remove();
      }
    });
  }
}

// Export a singleton instance
export const authUI = new AuthUI();
